import { FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import {
  authenticate,
  CognitoRequestError,
  confirmSignUp,
  getProfile,
  isGoogleLoginConfigured,
  resendConfirmationCode,
  signUp,
} from '../../auth/cognito';
import { createMemberProfile } from '../../auth/memberApi';
import { phoneInputDigitsOnly } from '../../auth/phone';
import './auth.css';

const getErrorMessage = (requestError: unknown, fallback: string) =>
  requestError instanceof Error ? requestError.message : fallback;

const Enroll = () => {
  const { isAuthenticated, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isReseller, setIsReseller] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/profile" replace />;
  }

  const handleEnrollment = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    setIsSubmitting(true);

    const normalizedWebsite = website.trim();
    if (normalizedWebsite) {
      try {
        const parsedWebsite = new URL(normalizedWebsite);
        if (!['http:', 'https:'].includes(parsedWebsite.protocol)) {
          throw new Error('Invalid protocol');
        }
      } catch {
        setError('Enter a valid website URL starting with http:// or https://.');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await signUp(email, password, name, phoneNumber);
      if (response.UserConfirmed) {
        navigate('/login', { replace: true });
        return;
      }
      setIsConfirming(true);
      setMessage('Check your email for the confirmation code.');
    } catch (requestError) {
      if (
        requestError instanceof CognitoRequestError
        && requestError.code === 'UsernameExistsException'
      ) {
        try {
          await resendConfirmationCode(email);
          setIsConfirming(true);
          setMessage('That account already exists. We sent a new confirmation code.');
        } catch (resendError) {
          if (
            resendError instanceof CognitoRequestError
            && ['InvalidParameterException', 'NotAuthorizedException'].includes(resendError.code)
          ) {
            setError('That account already exists and appears to be confirmed. Please log in.');
          } else {
            setError(getErrorMessage(resendError, 'Unable to resend the confirmation code.'));
          }
        }
      } else {
        setError(getErrorMessage(requestError, 'Unable to enroll.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmation = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await confirmSignUp(email, confirmationCode);
      const tokens = await authenticate(email, password);
      const profile = await getProfile(tokens.accessToken);
      await createMemberProfile(tokens.accessToken, {
        name: name || profile.name,
        companyName,
        website: website.trim(),
        phoneNumber,
        emailOptIn: true,
        isReseller,
      });
      navigate('/login', {
        replace: true,
        state: { message: 'Enrollment confirmed. You can now log in.' },
      });
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to confirm enrollment.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await resendConfirmationCode(email);
      setMessage('A new confirmation code was sent.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Unable to resend the code.'));
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-hero">
        <p>Join Hogcloud</p>
        <h1>Create your member account.</h1>
        <span>Enroll with Google or create an account with your email address.</span>
      </div>
      <div className="auth-card">
        <form
          className="auth-form"
          onSubmit={isConfirming ? handleConfirmation : handleEnrollment}
        >
          {error && <p className="auth-message error" role="alert">{error}</p>}
          {message && <p className="auth-message success">{message}</p>}

          {isGoogleLoginConfigured && !isConfirming && (
            <>
              <button
                className="auth-google"
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  setError('');
                  setIsSubmitting(true);
                  loginWithGoogle().catch((loginError) => {
                    setError(getErrorMessage(loginError, 'Google enrollment failed.'));
                    setIsSubmitting(false);
                  });
                }}
              >
                Enroll with Google
              </button>
              <div className="auth-divider"><span>or use email</span></div>
              <p className="auth-help">
                Google supplies your verified email and name. You can add company and contact details
                from your profile after enrollment.
              </p>
            </>
          )}

          {!isConfirming ? (
            <>
              <div className="auth-field">
                <label htmlFor="enroll-name">Full name</label>
                <input
                  id="enroll-name"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="enroll-email">Email</label>
                <input
                  id="enroll-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="enroll-company">Company Name</label>
                <input
                  id="enroll-company"
                  autoComplete="organization"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="enroll-website">Website</label>
                <input
                  id="enroll-website"
                  type="url"
                  autoComplete="url"
                  placeholder="https://example.com"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </div>
              <div className="auth-field">
                <label htmlFor="enroll-phone">Phone number</label>
                <input
                  id="enroll-phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[2-9][0-9]{9}"
                  placeholder="4795550199"
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(phoneInputDigitsOnly(event.target.value))}
                />
                <p className="auth-help">
                  Optional. Enter 10 digits starting with area code, like 4795550199.
                </p>
              </div>
              <div className="auth-field">
                <label htmlFor="enroll-password">Password</label>
                <input
                  id="enroll-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <p className="auth-help">
                  At least 12 characters with uppercase, lowercase, a number, and a symbol.
                </p>
              </div>
              <label className="auth-checkbox" htmlFor="enroll-reseller">
                <input
                  checked={isReseller}
                  id="enroll-reseller"
                  onChange={(event) => setIsReseller(event.target.checked)}
                  type="checkbox"
                />
                <span>I am a reseller for Hogcloud</span>
              </label>
              {isReseller && (
                <p className="auth-disclaimer">
                  Your tax ID will be requested. Please fill out the required form stating
                  that you are licensed to sell goods and services in Arkansas and email it
                  to <a href="mailto:sales@hogcloud.net">sales@hogcloud.net</a>.{' '}
                  <a
                    href="/assets/docs/AR-Sales-ExemptionCertificate.pdf"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Open the Arkansas sales exemption certificate
                  </a>
                  .
                </p>
              )}
            </>
          ) : (
            <div className="auth-field">
              <label htmlFor="confirmation-code">Confirmation code</label>
              <input
                id="confirmation-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={confirmationCode}
                onChange={(event) => setConfirmationCode(event.target.value)}
              />
            </div>
          )}

          <button className="auth-submit" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? 'Please wait...'
              : isConfirming
                ? 'Confirm enrollment'
                : 'Enroll'}
          </button>

          {isConfirming && (
            <button className="auth-link-button" type="button" onClick={handleResend}>
              Resend confirmation code
            </button>
          )}
          <p className="auth-switch">
            Already enrolled? <Link to="/login">Login</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default Enroll;
